/**
 * InlineEditableCell — reusable inline-cell-edit primitive.
 *
 * Used by AdditionalTableTab (Etap E) to edit data-row cells in-place.
 * Designed to be reused by AdditionalTablesTab (Etap F) for column-definition
 * inline editing.
 *
 * Props:
 *  - value           — current cell value (any)
 *  - onCommit        — called with the new value when user presses Enter or blurs
 *                      (only fired when value actually changed, per isEqual)
 *  - onCancel        — optional; called when user presses Escape
 *  - isEqual         — optional equality comparator(committedValue, draft) → bool.
 *                      When omitted, plain === is used. Supply a type-aware
 *                      comparator to avoid false-changes for date/numeric types
 *                      where the display form differs from the stored ISO string.
 *  - renderInput     — render function: (value, onChange, onKeyDown, onBlur) => ReactElement
 *                      caller supplies the typed input widget; this component
 *                      manages focus, keyboard events, and commit/cancel logic
 *  - renderDisplay   — optional; renders the read-only display form. Defaults to
 *                      String(value) (or '—' for null/undefined).
 *  - 'data-testid'   — forwarded to the wrapper <span>
 */
import {
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

/** Narrow an unknown onCommit return to a thenable (async commit path). */
function isPromiseLike<T>(x: unknown): x is PromiseLike<T> {
  return (
    typeof x === 'object' && x !== null && typeof (x as { then?: unknown }).then === 'function'
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseInlineCellEditOptions<T> {
  /** Current committed value of the cell. */
  committedValue: T;
  /**
   * Called with the new value when committing (only if value changed per isEqual).
   * May return a Promise: when it REJECTS, the cell stays in edit mode and the
   * rejection message (a user-ready string) is surfaced inline; on resolve the
   * cell closes. Returning void keeps the original fire-and-forget behavior
   * (cell closes immediately, regardless of any later async failure).
   */
  onCommit: (newValue: T) => void | Promise<void>;
  /**
   * Optional equality comparator. Return true when committedValue and draft
   * are considered the same (no PATCH should fire).
   *
   * Default: strict === (fine for string/boolean/null fields).
   * For date fields: compare on the 10-char slice ('YYYY-MM-DD').
   * For timestamp: compare on the 16-char slice ('YYYY-MM-DDTHH:MM').
   * For numeric/integer: coerce both sides to Number before comparing.
   */
  isEqual?: (committedValue: T, draft: T) => boolean;
}

export interface UseInlineCellEditResult<T> {
  /** Whether the cell is currently in edit mode. */
  isEditing: boolean;
  /** The current draft value (only meaningful while isEditing). */
  draftValue: T;
  /** Update the draft value (onChange handler). */
  setDraftValue: (v: T) => void;
  /** Activate edit mode. */
  startEdit: () => void;
  /** Commit the draft (fires onCommit if changed), exit edit mode. */
  commit: () => void;
  /** Cancel edit without firing onCommit. */
  cancel: () => void;
  /** Keyboard handler: Enter → commit, Escape → cancel. */
  handleKeyDown: (e: KeyboardEvent) => void;
  /**
   * Ref to attach to the input element so it can be auto-focused on edit start.
   * Wire this ref to the <input> element inside renderInput.
   */
  inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /**
   * Commit error message (from a rejected async onCommit), or null. While set,
   * the cell stays in edit mode so the user can correct the value.
   */
  error: string | null;
  /**
   * True while an async onCommit is in flight. During this window the draft is
   * frozen (edits ignored) and commit/cancel are no-ops; expose it so callers
   * can disable the input.
   */
  isCommitting: boolean;
}

export function useInlineCellEdit<T>({
  committedValue,
  onCommit,
  isEqual,
}: UseInlineCellEditOptions<T>): UseInlineCellEditResult<T> {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValueState] = useState<T>(committedValue);
  const [error, setError] = useState<string | null>(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // Guards a re-entrant commit() (e.g. blur firing after Enter) while an async
  // onCommit is still pending, so the value is not committed twice.
  const committingRef = useRef(false);
  // Avoid setState after unmount if an async onCommit settles late.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const defaultIsEqual = useCallback(
    (a: T, b: T): boolean => (isEqual ? isEqual(a, b) : a === b),
    [isEqual],
  );

  // Update the draft; clear any lingering commit error. Ignored while an async
  // commit is in flight so in-progress typing can't be silently discarded on close.
  const setDraftValue = useCallback((v: T) => {
    if (committingRef.current) return;
    setError(null);
    setDraftValueState(v);
  }, []);

  const startEdit = useCallback(() => {
    setError(null);
    setDraftValueState(committedValue);
    setIsEditing(true);
  }, [committedValue]);

  const commit = useCallback(() => {
    // m3: one-shot guard — bail if we already left edit mode (Enter committed,
    // input unmounted, its blur then fires commit() again).
    if (!isEditing) return;
    // async in-flight guard — ignore a re-entrant commit() while a pending
    // async onCommit has not settled yet.
    if (committingRef.current) return;

    if (defaultIsEqual(committedValue, draftValue)) {
      setError(null);
      setIsEditing(false);
      return;
    }

    const result = onCommit(draftValue);
    if (isPromiseLike<void>(result)) {
      // Async consumer: keep the cell open until the promise settles. On
      // rejection stay in edit mode and surface the consumer-provided message.
      committingRef.current = true;
      setIsCommitting(true);
      void Promise.resolve(result).then(
        () => {
          committingRef.current = false;
          if (!isMountedRef.current) return;
          setIsCommitting(false);
          setError(null);
          setIsEditing(false);
        },
        (reason: unknown) => {
          committingRef.current = false;
          if (!isMountedRef.current) return;
          setIsCommitting(false);
          setError(reason instanceof Error ? reason.message : String(reason));
          inputRef.current?.focus();
        },
      );
    } else {
      // Sync consumer: original fire-and-forget behavior (close immediately).
      setError(null);
      setIsEditing(false);
    }
  }, [isEditing, committedValue, draftValue, onCommit, defaultIsEqual]);

  const cancel = useCallback(() => {
    // Don't cancel/rollback while an async commit is pending — its result would
    // land on a closed cell and the error could not be shown.
    if (committingRef.current) return;
    setError(null);
    setIsEditing(false);
    setDraftValueState(committedValue);
  }, [committedValue]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [commit, cancel],
  );

  return {
    isEditing,
    draftValue,
    setDraftValue,
    startEdit,
    commit,
    cancel,
    handleKeyDown,
    inputRef,
    error,
    isCommitting,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface InlineEditableCellProps<T> {
  /** Current committed value. */
  value: T;
  /**
   * Called with the new value on commit (only when changed per isEqual).
   * Return a Promise to keep the cell open until it settles: on reject the cell
   * stays in edit mode and shows the rejection message inline. Return void for
   * fire-and-forget (cell closes immediately).
   */
  onCommit: (newValue: T) => void | Promise<void>;
  /**
   * Optional equality comparator forwarded to useInlineCellEdit.
   * Supply for date/timestamp/numeric fields so format differences don't
   * trigger spurious PATCHes.
   */
  isEqual?: (committedValue: T, draft: T) => boolean;
  /**
   * Optional callback invoked when the user cancels (presses Escape).
   */
  onCancel?: () => void;
  /**
   * Render the editable input widget.
   *
   * Receives:
   *  - draftValue   — current in-progress value
   *  - onChange     — call to update draft
   *  - onKeyDown    — attach to the input for Enter/Esc handling
   *  - onBlur       — attach to the input to commit on blur
   *  - ref          — attach to the focusable element for auto-focus
   *  - isCommitting — true while an async onCommit is pending; disable the input
   *
   * IMPORTANT: attach the supplied ref to the focusable <input>/<textarea>
   * so that the component can manage focus correctly.
   */
  renderInput: (
    draftValue: T,
    onChange: (v: T) => void,
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void,
    onBlur: () => void,
    ref: RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
    isCommitting: boolean,
  ) => ReactElement | null;
  /**
   * Render the read-only display of the cell.
   * If omitted, String(value) is rendered (or '—' for null/undefined).
   */
  renderDisplay?: (value: T) => ReactElement | string;
  /** data-testid forwarded to the wrapper element. */
  'data-testid'?: string;
  /** Extra CSS class on the wrapper. */
  className?: string;
}

export function InlineEditableCell<T>({
  value,
  onCommit,
  isEqual,
  onCancel,
  renderInput,
  renderDisplay,
  'data-testid': testId,
  className,
}: InlineEditableCellProps<T>) {
  const {
    isEditing,
    draftValue,
    setDraftValue,
    startEdit,
    commit,
    handleKeyDown: baseHandleKeyDown,
    inputRef,
    error,
    isCommitting,
  } = useInlineCellEdit({ committedValue: value, onCommit, isEqual });

  // Wire onCancel so callers can react to Escape.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape') {
        // Frozen while an async commit is pending (cancel() would no-op anyway).
        if (isCommitting) {
          e.preventDefault();
          return;
        }
        // baseHandleKeyDown will call cancel(); also notify the caller.
        baseHandleKeyDown(e);
        onCancel?.();
      } else {
        baseHandleKeyDown(e);
      }
    },
    [baseHandleKeyDown, onCancel, isCommitting],
  );

  // Auto-focus the input element via the ref when entering edit mode.
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing, inputRef]);

  const displayContent =
    renderDisplay != null ? renderDisplay(value) : value != null ? String(value) : '—';

  if (isEditing) {
    return (
      <span data-testid={testId} className={className} style={{ display: 'contents' }}>
        {renderInput(draftValue, setDraftValue, handleKeyDown, commit, inputRef, isCommitting)}
        {error != null && (
          <span
            role="alert"
            data-testid={testId ? `${testId}-error` : undefined}
            className="mt-0.5 block text-xs text-red-600 dark:text-red-400"
            title={error}
          >
            {error}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      data-testid={testId}
      className={className}
      onClick={startEdit}
      style={{ cursor: 'text', display: 'block', minHeight: '1.25rem' }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'F2') startEdit();
      }}
    >
      {displayContent}
    </span>
  );
}
